import {
  BodyDefault,
  Button,
  Checkbox,
  LabelDefault,
  Menu,
  MetaDefault,
  Spinner,
  Tooltip,
} from '@terra/ui-design-system';
import { HStack, VStack, Box, token } from '@terra/ui-styled-system';
import {
  UserIcon,
  ArrowDown01Icon,
  InternetIcon,
  ShieldBanIcon,
  CheckmarkCircle01Icon,
  Alert01Icon,
} from '@terra/ui-icons';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useSelectedTarget } from '../../store/projectStore';
import { useProxyStore } from '../../store/proxy-store';
import { useCredentialVerificationSync } from '../../hooks/useCredentialVerificationSync';
import {
  getBrowserLaunchReadiness,
  isCredentialBrowserLaunchable,
} from './browserLaunchReadiness';
import { useLaunchBrowser } from './useLaunchBrowser';

export function TopBarBrowserLauncher() {
  const t = useTranslations('layout');
  const tBrowser = useTranslations('proxy.browser');
  const startBrowserLabel = t('startBrowser');
  const selectedTarget = useSelectedTarget();
  const stealthLaunchEnabled = useProxyStore(
    (state) => state.settings.stealthLaunchEnabled,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [stealthMode, setStealthMode] = useState(false);
  const {
    launchBrowser: handleLaunchBrowser,
    isLaunching,
    isProxyRunning,
    hasEnabledSettingsMtls,
    credentials,
    cachedCredentials,
    isLoadingCredentials,
  } = useLaunchBrowser();

  useEffect(() => {
    if (!stealthLaunchEnabled) {
      setStealthMode(false);
    }
  }, [stealthLaunchEnabled]);

  useCredentialVerificationSync({
    applicationId: selectedTarget?.id,
  });

  const cachedCredentialsById = useMemo(() => {
    return new Map(
      cachedCredentials.map((cachedCredential) => [
        cachedCredential.id,
        cachedCredential,
      ]),
    );
  }, [cachedCredentials]);
  // Filter to only show credentials allowed for browser launch:
  // enabled + verified + having cached auth data (same gate as replay semantics).
  const enabledCredentials = useMemo(() => {
    if (!Array.isArray(credentials)) {
      return [];
    }

    return credentials.filter((credential) =>
      isCredentialBrowserLaunchable({
        credential,
        cached: cachedCredentialsById.get(credential.id),
      }),
    );
  }, [credentials, cachedCredentialsById]);
  const hasCredentials = enabledCredentials.length > 0;
  const isDisabled = !isProxyRunning || isLaunching;
  const isStealth = stealthLaunchEnabled && stealthMode;
  const triggerContent = useMemo(() => {
    if (isLaunching) {
      return <Spinner size="xs" />;
    }

    return (
      <InternetIcon
        size={16}
        color={isStealth ? token('colors.fg.accent.default') : undefined}
      />
    );
  }, [isLaunching, isStealth]);

  // Simple button only after credentials resolve with nothing to launch with.
  // Keep the menu during the initial fetch (isLoading, not background refetch)
  // so we do not flash plain → menu and users cannot launch before we know
  // whether credentials exist. Menu also keeps "no client TLS" reachable.
  if (
    !hasCredentials &&
    !hasEnabledSettingsMtls &&
    !isLoadingCredentials &&
    !stealthLaunchEnabled
  ) {
    return (
      <Tooltip title={startBrowserLabel}>
        <Button
          data-tour-id="launch-browser-action"
          variant="ghost"
          size="xs"
          onClick={() => handleLaunchBrowser()}
          disabled={isDisabled}
          aria-label={startBrowserLabel}
        >
          {triggerContent}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Menu.Root
      positioning={{
        placement: 'bottom-end',
        strategy: 'fixed',
        getAnchorRect: () =>
          triggerRef.current?.getBoundingClientRect() ?? null,
      }}
    >
      <Tooltip title={startBrowserLabel}>
        <Menu.Trigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="xs"
            disabled={isDisabled}
            aria-label={startBrowserLabel}
          >
            {triggerContent}
            <ArrowDown01Icon size={10} />
          </Button>
        </Menu.Trigger>
      </Tooltip>
      <Menu.Positioner>
        <Menu.Content minW="220px" py="1">
          {stealthLaunchEnabled && (
            <>
              <HStack
                px="2"
                py="1.5"
                gap="3"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <VStack flex="1" minW="0" gap="0" alignItems="flex-start">
                  <MetaDefault>{tBrowser('stealthMode')}</MetaDefault>
                  <MetaDefault color="fg.muted">
                    {tBrowser('stealthDescription')}
                  </MetaDefault>
                </VStack>
                <Checkbox
                  size="sm"
                  checked={stealthMode}
                  onCheckedChange={(details) =>
                    setStealthMode(!!details.checked)
                  }
                  aria-label={tBrowser('stealthMode')}
                  flexShrink={0}
                />
              </HStack>
              <Menu.Separator />
            </>
          )}

          <Menu.Item
            value="launch-plain"
            data-tour-id="launch-browser-action"
            onClick={() =>
              handleLaunchBrowser(undefined, { stealthMode: isStealth })
            }
            fontSize="xs"
            py="1"
            px="2"
          >
            <HStack gap="1.5">
              <InternetIcon
                size={14}
                color={
                  isStealth ? token('colors.fg.accent.default') : undefined
                }
              />
              <LabelDefault>{tBrowser('launchBrowser')}</LabelDefault>
            </HStack>
          </Menu.Item>

          {hasEnabledSettingsMtls && (
            <Menu.Item
              value="launch-no-mtls"
              data-tour-id="launch-browser-action"
              onClick={() =>
                handleLaunchBrowser(undefined, {
                  excludeSettingsCerts: true,
                  stealthMode: isStealth,
                })
              }
              fontSize="xs"
              py="1"
              px="2"
            >
              <HStack gap="1.5">
                <ShieldBanIcon size={12} />
                <Box flex="1">
                  <LabelDefault>Launch Browser (no client TLS)</LabelDefault>
                  <MetaDefault color="fg.neutral.muted">
                    Skip settings-configured certificates for this session
                  </MetaDefault>
                </Box>
              </HStack>
            </Menu.Item>
          )}

          {enabledCredentials.length > 0 && (
            <>
              <Menu.Separator />
              <Menu.ItemGroup>
                <Menu.ItemGroupLabel py="0.5" px="2">
                  <MetaDefault color="fg.neutral.muted">
                    Launch with Credential
                  </MetaDefault>
                </Menu.ItemGroupLabel>
                {enabledCredentials.map((credential) => {
                  const cachedCredential = cachedCredentialsById.get(
                    credential.id,
                  );
                  const readiness = getBrowserLaunchReadiness(cachedCredential);
                  const label =
                    credential.username && credential.role
                      ? `${credential.username} (${credential.role})`
                      : credential.username || credential.role || 'User';

                  return (
                    <Menu.Item
                      key={credential.id}
                      value={`launch-${credential.id}`}
                      data-tour-id="launch-browser-action"
                      onClick={() =>
                        handleLaunchBrowser(credential, {
                          stealthMode: isStealth,
                        })
                      }
                      fontSize="xs"
                      py="1"
                      px="2"
                    >
                      <HStack gap="1.5" w="full" alignItems="center">
                        <Box flexShrink={0} display="inline-flex">
                          <UserIcon size={12} />
                        </Box>
                        <LabelDefault flex="1" minW="0" truncate>
                          {label}
                        </LabelDefault>
                        {readiness && (
                          <HStack gap="1" flexShrink={0}>
                            {readiness === 'ready' ? (
                              <Tooltip title="Browser session cached — ready to launch">
                                <CheckmarkCircle01Icon size={12} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Auth tokens only — re-verify to capture full browser session">
                                <Alert01Icon size={12} />
                              </Tooltip>
                            )}
                          </HStack>
                        )}
                      </HStack>
                    </Menu.Item>
                  );
                })}
              </Menu.ItemGroup>
            </>
          )}

          {isLoadingCredentials && (
            <>
              <Menu.Separator />
              <Box py="2" px="2">
                <HStack gap="2">
                  <Spinner size="xs" />
                  <BodyDefault color="fg.neutral.muted">
                    Loading credentials...
                  </BodyDefault>
                </HStack>
              </Box>
            </>
          )}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
}
